import config from '@/utils/ApiUtils'
import { fetchWithToken } from '../utils/Keycloak'

const request = (path, options = {}) =>
  fetchWithToken(`${config().stockBackend}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options
  })
    .then(response => response.ok ? response.json() : response.text().then(text => {
      throw new Error(`Erreur ${response.status}: ${text}`)
    }))

const StockService = {

  /**
   * Récupère tous les produits depuis la base PG (après sync)
   * GET /inventory/products
   */
  getProducts() {
    return request('/products')
  },

  /**
   * Déclenche une synchro Pennylane → PG
   * POST /inventory/sync
   */
  syncProducts() {
    return request('/sync', { method: 'POST' })
  },

  /**
   * État courant du stock : Σ mouvements − expéditions magasins (shipments),
   * avec en-commande, seuil d'alerte, consommation moyenne 30 j et couverture.
   * GET /inventory/current-state
   */
  getCurrentState() {
    return request('/current-state')
  },

  /**
   * Suit ou masque un produit (services, 2e tablette… hors stock)
   * PUT /inventory/products/:productName/tracked
   */
  setTracked(productName, tracked) {
    return request(`/products/${encodeURIComponent(productName)}/tracked`, {
      method: 'PUT',
      body: JSON.stringify({ tracked })
    })
  },

  setDetails(productName, { category, leadTimeDays, orderContacts, orderUrl, orderNotes }) {
    return request(`/products/${encodeURIComponent(productName)}/details`, {
      method: 'PUT',
      body: JSON.stringify({ category, leadTimeDays, orderContacts, orderUrl, orderNotes })
    })
  },

  getCategories() {
    return request('/categories')
  },

  /**
   * Lieux de stockage (Guérande, Bureau Nantes, Espace de stockage Nantes)
   * GET /inventory/locations
   */
  getLocations() {
    return request('/locations')
  },

  /**
   * Dernier comptage de chaque produit dans chaque lieu
   * GET /inventory/counts/latest
   */
  getLatestCounts() {
    return request('/counts/latest')
  },

  /**
   * Sorties d'un bon de livraison manuel déjà généré, dans le lieu choisi
   * POST /inventory/delivery-notes
   */
  recordDeliveryNote({ ref, customerName, blFile, locationId, createdBy, lines }) {
    return request('/delivery-notes', {
      method: 'POST',
      body: JSON.stringify({ ref, customerName, blFile, locationId, createdBy, lines })
    })
  },

  /**
   * Lieu d'une réception ou de la sortie d'un bon de livraison manuel
   * PUT /inventory/movements/:movementId/location
   */
  setMovementLocation(movementId, locationId, updatedBy) {
    return request(`/movements/${encodeURIComponent(movementId)}/location`, {
      method: 'PUT',
      body: JSON.stringify({ locationId, updatedBy })
    })
  },

  /**
   * Lieu d'où sort toute une commande magasin (produits et carton) ; efface les lieux choisis produit par produit
   * PUT /inventory/shipments/:rayon/:idShipment/location
   */
  setShipmentLocation(rayon, idShipment, locationId, updatedBy) {
    return request(`/shipments/${encodeURIComponent(rayon)}/${encodeURIComponent(idShipment)}/location`, {
      method: 'PUT',
      body: JSON.stringify({ locationId, updatedBy })
    })
  },

  /**
   * Lieu de chaque produit d'une commande magasin, carton compris : lines = [{ productName, locationId }]
   * PUT /inventory/shipments/:rayon/:idShipment/products/location
   */
  setShipmentProductLocations(rayon, idShipment, lines, updatedBy) {
    return request(`/shipments/${encodeURIComponent(rayon)}/${encodeURIComponent(idShipment)}/products/location`, {
      method: 'PUT',
      body: JSON.stringify({ lines, updatedBy })
    })
  },

  transfer({ productName, fromLocationId, toLocationId, quantity, comment, createdBy }) {
    return request('/transfers', {
      method: 'POST',
      body: JSON.stringify({ productName, fromLocationId, toLocationId, quantity, comment, createdBy })
    })
  },

  /**
   * Inventaire par lieu : comptages + ajustement de l'écart total, en une transaction
   * POST /inventory/counts
   */
  recordInventory({ lines, createdBy }) {
    return request('/counts', {
      method: 'POST',
      body: JSON.stringify({ lines, createdBy })
    })
  },

  /**
   * Crée un mouvement : réception directe (quantité > 0) ou ajustement d'inventaire (delta signé)
   * POST /inventory/movements
   */
  addMovement({ productName, type, quantity, comment, createdBy, locationId, clientId }) {
    return request('/movements', {
      method: 'POST',
      body: JSON.stringify({ productName, type, quantity, comment, createdBy, locationId, clientId })
    })
  },

  /**
   * Journal unifié : mouvements locaux + sorties shipments (quantités négatives)
   * GET /inventory/movements?productName&startDate&endDate
   */
  getJournal({ productName, startDate, endDate } = {}) {
    const params = new URLSearchParams()
    if (productName) params.set('productName', productName)
    if (startDate) params.set('startDate', startDate)
    if (endDate) params.set('endDate', endDate)
    const query = params.toString() ? `?${params.toString()}` : ''
    return request(`/movements${query}`)
  },

  /**
   * Pose ou retire (null) le seuil d'alerte d'un produit
   * PUT /inventory/products/:productName/threshold
   */
  setThreshold(productName, alertThreshold) {
    return request(`/products/${encodeURIComponent(productName)}/threshold`, {
      method: 'PUT',
      body: JSON.stringify({ alertThreshold })
    })
  },

  /**
   * Commandes fournisseur (réassort interne Hublot)
   */
  getOrders(status = null) {
    return request(`/orders${status ? `?status=${status}` : ''}`)
  },

  createOrder({ supplier, status, expectedDate, comment, createdBy, lines }) {
    return request('/orders', {
      method: 'POST',
      body: JSON.stringify({ supplier, status, expectedDate, comment, createdBy, lines })
    })
  },

  updateOrder(orderId, payload) {
    return request(`/orders/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    })
  },

  /** Réception totale ou partielle : lines = [{ lineId, quantity }] */
  receiveOrder(orderId, receivedBy, lines, locationId) {
    return request(`/orders/${orderId}/receive`, {
      method: 'POST',
      body: JSON.stringify({ receivedBy, lines, locationId })
    })
  }
}

export default StockService
